/**
 * Self-debate upload preflight — runs BEFORE we open the recorder/uploader
 * so users see a retryable error instead of the analyzer failing mid-stream.
 *
 * Limits are conservative defaults; actual edge function limits are larger,
 * so a "passing" preflight virtually guarantees the upload will be accepted.
 */

export type CaptureKind = "text" | "audio" | "video";

export interface PreflightLimits {
  /** Max bytes for a text payload (UTF-8). */
  textMaxBytes: number;
  /** Max bytes for an audio file. */
  audioMaxBytes: number;
  /** Max bytes for a video file. */
  videoMaxBytes: number;
}

export const DEFAULT_PREFLIGHT_LIMITS: PreflightLimits = {
  textMaxBytes: 64 * 1024, // 64 KB transcript
  audioMaxBytes: 25 * 1024 * 1024, // 25 MB audio
  videoMaxBytes: 200 * 1024 * 1024, // 200 MB video
};

/** Supported codecs / containers per capture kind. Kept in sync with the
 *  analyzer pipeline (Mux + Deepgram + Whisper). */
export const SUPPORTED_CODECS: Record<CaptureKind, string[]> = {
  text: ["text/plain", "text/markdown", "application/json"],
  audio: ["audio/webm (opus)", "audio/mp4 (aac)", "audio/mpeg", "audio/wav"],
  video: ["video/mp4 (h264+aac)", "video/webm (vp9+opus)", "video/quicktime"],
};

/** Human-readable size limits (used in UI hints + error messages). */
export function friendlyLimit(kind: CaptureKind, limits: PreflightLimits = DEFAULT_PREFLIGHT_LIMITS): string {
  const max =
    kind === "text" ? limits.textMaxBytes
    : kind === "audio" ? limits.audioMaxBytes
    : limits.videoMaxBytes;
  return max >= 1024 * 1024
    ? `${(max / (1024 * 1024)).toFixed(0)} MB`
    : `${(max / 1024).toFixed(0)} KB`;
}

/** Per-file actionable retry guidance — surfaced beside the error in the UI. */
export const RETRY_GUIDANCE: Record<CaptureKind, string[]> = {
  text: [
    "Trim filler paragraphs or split into a separate take",
    "Paste plain text — drop image/PDF attachments",
    "Save the transcript as .txt and re-upload",
  ],
  audio: [
    "Re-encode at 96 kbps mono opus or 128 kbps AAC",
    "Trim long silences from the start/end",
    "Split a multi-section take into separate clips",
  ],
  video: [
    "Drop resolution to 1080p or 720p before re-uploading",
    "Re-encode at CRF 26 (h264) or VP9 medium",
    "Split into <90s segments — analyzer scores per-segment anyway",
  ],
};

export interface PreflightInput {
  kind: CaptureKind;
  /** For text: UTF-8 length. For audio/video: byte size of the blob. */
  sizeBytes: number;
  /** Optional MIME hint, used in error messages. */
  mime?: string;
  /** Human-friendly file label (used to identify the failing file). */
  label?: string;
}

export interface PreflightResult {
  ok: boolean;
  /** Human-readable failure reason; empty when ok. */
  error?: string;
  /** Stable machine code so tests / retry UIs can assert. */
  code?: "EMPTY" | "OVERSIZE" | "UNKNOWN_KIND" | "UNSUPPORTED_CODEC";
  /** Effective max bytes for this kind. */
  maxBytes: number;
  /** Whether the failure is retryable (always true for size errors). */
  retryable: boolean;
  /** Per-file retry guidance the UI can render as bullet points. */
  guidance?: string[];
  /** Supported codecs for this capture kind, for hint copy. */
  supportedCodecs?: string[];
}

export function runUploadPreflight(
  input: PreflightInput,
  limits: PreflightLimits = DEFAULT_PREFLIGHT_LIMITS,
): PreflightResult {
  const max =
    input.kind === "text"
      ? limits.textMaxBytes
      : input.kind === "audio"
      ? limits.audioMaxBytes
      : input.kind === "video"
      ? limits.videoMaxBytes
      : 0;

  if (max === 0) {
    return {
      ok: false,
      code: "UNKNOWN_KIND",
      error: `Unsupported capture type: ${input.kind}`,
      maxBytes: 0,
      retryable: false,
    };
  }

  if (input.sizeBytes <= 0) {
    return {
      ok: false,
      code: "EMPTY",
      error: `${input.kind} payload is empty — record or paste content before continuing.`,
      maxBytes: max,
      retryable: true,
      guidance: RETRY_GUIDANCE[input.kind],
      supportedCodecs: SUPPORTED_CODECS[input.kind],
    };
  }

  if (input.sizeBytes > max) {
    const fmt = (n: number) =>
      n >= 1024 * 1024
        ? `${(n / (1024 * 1024)).toFixed(1)} MB`
        : `${(n / 1024).toFixed(0)} KB`;
    return {
      ok: false,
      code: "OVERSIZE",
      error: `Your ${input.kind} is ${fmt(input.sizeBytes)} — limit is ${fmt(
        max,
      )}. Trim the take or split it and retry.`,
      maxBytes: max,
      retryable: true,
      guidance: RETRY_GUIDANCE[input.kind],
      supportedCodecs: SUPPORTED_CODECS[input.kind],
    };
  }

  // Optional codec sniff if a MIME hint was provided.
  if (input.mime) {
    const lower = input.mime.toLowerCase();
    const accepted = SUPPORTED_CODECS[input.kind].some((c) =>
      lower.startsWith(c.split(" ")[0]?.toLowerCase() ?? ""),
    );
    if (!accepted) {
      return {
        ok: false,
        code: "UNSUPPORTED_CODEC",
        error: `MIME ${input.mime} is not a supported ${input.kind} codec.`,
        maxBytes: max,
        retryable: true,
        guidance: RETRY_GUIDANCE[input.kind],
        supportedCodecs: SUPPORTED_CODECS[input.kind],
      };
    };
  }

  return { ok: true, maxBytes: max, retryable: true };
}

export interface BatchPreflightResult {
  ok: boolean;
  /** Per-file results, in input order. */
  results: Array<PreflightResult & { label: string; kind: CaptureKind }>;
  /** Index + label of the first failing file (when not ok). */
  firstFailure?: { index: number; label: string; kind: CaptureKind; result: PreflightResult };
  /** Aggregate count of failed files. */
  failedCount: number;
}

/**
 * Runs preflight against a batch of files and short-circuits the UI with
 * the first failing file so users know exactly which one to fix.
 *
 * Important: we still validate every file (not stop at the first) so the
 * Try-again button can re-run the same batch and surface a different
 * first-failure once the previous one is fixed.
 */
export function runBatchUploadPreflight(
  inputs: PreflightInput[],
  limits: PreflightLimits = DEFAULT_PREFLIGHT_LIMITS,
): BatchPreflightResult {
  const results = inputs.map((input, idx) => {
    const result = runUploadPreflight(input, limits);
    return {
      ...result,
      label: input.label ?? `${input.kind} #${idx + 1}`,
      kind: input.kind,
    };
  });
  const firstFailureIdx = results.findIndex((r) => !r.ok);
  const failedCount = results.filter((r) => !r.ok).length;
  return {
    ok: firstFailureIdx === -1,
    results,
    firstFailure:
      firstFailureIdx >= 0
        ? {
            index: firstFailureIdx,
            label: results[firstFailureIdx].label,
            kind: results[firstFailureIdx].kind,
            result: results[firstFailureIdx],
          }
        : undefined,
    failedCount,
  };
}